/**
 * MusicLyricsPanel - フルスクリーンプレイヤーの「歌詞」タブ
 *
 * 現在のトラックの歌詞を取得して表示する。
 */

import { useEffect, useMemo, useState } from 'react'

import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useMusicStore } from '@/store/useMusicStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'
import type { AudiobookTranscript } from '@/types/audiobook-transcript'

import { MusicLyricsSyncToggle } from './lyrics-sync-toggle'
import { resolveLyricsSyncMode } from './lyrics-timing'
import { MusicLyricsView } from './lyrics-view'
import { useLyricsImport } from './use-lyrics-import'

type MusicLyricsPanelProps = {
  episodeId: number
  title: string
  currentTime: number
  onSeek: (seconds: number) => void
}

export const MusicLyricsPanel = ({ episodeId, title, currentTime, onSeek }: MusicLyricsPanelProps) => {
  const [transcript, setTranscript] = useState<AudiobookTranscript | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const savedRevision = useLyricsImport((s) => s.savedRevision)
  const openLyricsImport = useLyricsImport((s) => s.openLyricsImport)
  const setPlayerOpen = useMusicStore((s) => s.setPlayerOpen)
  const preferredSyncMode = useUISettingsStore((s) => s.lyricsSyncMode)

  const syncMode = useMemo(
    () => (transcript ? resolveLyricsSyncMode(transcript.lines, preferredSyncMode) : preferredSyncMode),
    [transcript, preferredSyncMode],
  )

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    setTranscript(null)
    apiClient
      .get<AudiobookTranscript>(`/e/${episodeId}/lyrics`, { signal: controller.signal })
      .then((res) => setTranscript(res.data))
      .catch(() => setTranscript(null))
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [episodeId, savedRevision])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={32} />
      </div>
    )
  }

  if (!transcript) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-muted-foreground">
        歌詞がありません
        <Button
          variant="secondary"
          onClick={() => {
            setPlayerOpen(false)
            openLyricsImport({ episodeId, title })
          }}
        >
          サイトから追加
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <MusicLyricsSyncToggle lines={transcript.lines} />
      <div className="min-h-0 flex-1">
        <MusicLyricsView transcript={transcript} currentTime={currentTime} syncMode={syncMode} onSeek={onSeek} />
      </div>
    </div>
  )
}
